import { ConfirmationDialog } from '@/components/patterns/confirmation-dialog'
import LoadingButton from '@/components/ui/loading-button'
import { AdminHeaderActionButton, AdminPageHeader } from '@/components/patterns/admin-page'
import SortableReorderControls from '@/components/forms/SortableReorderControls'
import DeliveryRouteBuilderMap from '@/components/orders/DeliveryRouteBuilderMap'
import { getDeliveryRouteMapBucketsForOrders } from '@/components/orders/delivery-route-map-model'
import DeliveryRouteStopCard from '@/components/orders/DeliveryRouteStopCard'
import DeliveryRouteShareDialog from '@/components/orders/DeliveryRouteShareDialog'
import { buildDeliveryRoutePrintHtml } from '@/components/orders/delivery-route-print'
import {
  buildDeliveryRouteDisplayStops,
  buildDeliveryRouteStopPayload,
  getDeliveryRouteReorderSessionKey,
} from '@/components/orders/delivery-route-stops'
import {
  formatDeliveryRouteDateTime,
  getDeliveryRouteMissedCount,
  getDeliveryShareErrorMessage,
  getDeliveryRouteSelectionId,
  getSafeDayjs,
  isDeliveryRouteCompleted,
  isDeliveryRouteStarted,
} from '@/components/orders/delivery-share.utils'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Typography } from '@/components/ui/typography'
import { usePathHelper } from '@/hooks/usePathHelper'
import { usePrintWindow } from '@/hooks/usePrintWindow'
import {
  useRevokeDeliveryShareMutation,
  useUpdateDeliveryShareMutation,
} from '@/lib/services/delivery-shares/delivery-shares.query'
import { cn, getErrorMessage } from '@/lib/utils'
import {
  closestCenter,
  DndContext,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core'
import {
  arrayMove,
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { useNavigate } from '@tanstack/react-router'
import { useCopyToClipboard } from '@uidotdev/usehooks'
import {
  Copy,
  GripVertical,
  PencilLine,
  Printer,
  Route as RouteIcon,
  Sparkles,
  Trash2,
  X,
} from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { QRCodeCanvas } from 'qrcode.react'
import { toast } from 'sonner'
import { useDeliveryRouteDetailShare } from './-delivery-route-query'

function SortableRouteStopCard({
  isLocked = false,
  isReorderPending = false,
  isReordering = false,
  order,
  reorderableCount = 0,
  reorderableIndex = -1,
  stopNumber,
  onMoveDown,
  onMoveUp,
}) {
  const { attributes, isDragging, listeners, setNodeRef, transform, transition } = useSortable({
    id: String(getDeliveryRouteSelectionId(order)),
    disabled: !isReordering || isLocked || isReorderPending,
  })
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  }
  const showReorderControls = isReordering && !isLocked

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      className={cn(
        'flex min-w-0 gap-3',
        isReordering && 'items-stretch',
        isDragging && 'opacity-70'
      )}
    >
      {showReorderControls ? (
        <SortableReorderControls
          index={reorderableIndex}
          count={reorderableCount}
          onMoveUp={onMoveUp}
          onMoveDown={onMoveDown}
          listeners={listeners}
          transition={transition}
          disabled={isReorderPending}
          className="shrink-0 rounded-lg border bg-background px-1 py-2"
          preventLinkNavigation
        />
      ) : null}
      <div className="min-w-0 flex-1">
        <DeliveryRouteStopCard isEditing={isReordering} order={order} stopNumber={stopNumber} />
      </div>
    </div>
  )
}

function RouteMetricCard({ description, label, tone = 'neutral', value }) {
  const surface =
    tone === 'destructive'
      ? 'destructive'
      : tone === 'success'
        ? 'success'
        : tone === 'brand'
          ? 'brandSubtle'
          : 'default'

  return (
    <Card data-tone={tone} size="sm" surface={surface}>
      <CardHeader size="sm" className="space-y-1">
        <CardDescription size="sm">{label}</CardDescription>
        <CardTitle
          size="sm"
          className={cn(
            'tracking-tight',
            label === 'Last update' ? 'text-lg leading-tight sm:text-xl' : 'text-2xl'
          )}
        >
          {value}
        </CardTitle>
      </CardHeader>
      <CardContent size="sm" className="pt-0">
        <Typography variant="supporting" className="block">
          {description}
        </Typography>
      </CardContent>
    </Card>
  )
}

function getManualRouteStopOrderIds(routeStopOrderIds, manualRouteStopIds, lockedStopIdSet) {
  let manualStopIndex = 0

  const orderedStopIds = routeStopOrderIds.map((orderId) => {
    if (lockedStopIdSet.has(orderId)) {
      return orderId
    }

    const manualOrderId = manualRouteStopIds[manualStopIndex]
    manualStopIndex += 1

    return manualOrderId ?? orderId
  })

  return [...orderedStopIds, ...manualRouteStopIds.slice(manualStopIndex)]
}

function applyOptimizedRouteStopOrder(currentOrderIds, optimizedOrderIds) {
  const optimizedOrderIdSet = new Set(optimizedOrderIds)
  let optimizedOrderIndex = 0

  return currentOrderIds.map((orderId) => {
    if (!optimizedOrderIdSet.has(orderId)) {
      return orderId
    }

    const optimizedOrderId = optimizedOrderIds[optimizedOrderIndex]
    optimizedOrderIndex += 1

    return optimizedOrderId ?? orderId
  })
}

function getRouteOptimizationPositionKey(position) {
  const rawLat = position?.lat
  const rawLng = position?.lng
  const hasPosition =
    rawLat !== null &&
    typeof rawLat !== 'undefined' &&
    rawLat !== '' &&
    rawLng !== null &&
    typeof rawLng !== 'undefined' &&
    rawLng !== ''

  if (!hasPosition) {
    return null
  }

  const lat = Number(rawLat)
  const lng = Number(rawLng)
  return Number.isFinite(lat) && Number.isFinite(lng) ? `${lat.toFixed(6)}:${lng.toFixed(6)}` : null
}

function getRouteOptimizationOriginKey(driverLocation, floristLocation) {
  const driverPositionKey = getRouteOptimizationPositionKey(driverLocation)

  if (driverPositionKey) {
    return `driver:${driverPositionKey}`
  }

  const address = floristLocation?.address
  const floristPositionKey = getRouteOptimizationPositionKey(address)
  const floristAddress = [
    address?.line1,
    address?.line2,
    address?.city,
    address?.state,
    address?.postal_code,
    address?.country,
  ]
    .filter(Boolean)
    .join(', ')

  return `florist:${floristPositionKey ?? floristAddress}`
}

function getRouteOptimizationProblemKey(orderIds, routeStopById, driverLocation, floristLocation) {
  const stopKeys = orderIds
    .map((orderId) => {
      const order = routeStopById.get(orderId)
      const address = order?.delivery_address

      return [
        orderId,
        order?.is_delivered ? 'delivered' : '',
        order?.is_route_missed ? 'missed' : '',
        address?.line1 ?? '',
        address?.line2 ?? '',
        address?.city ?? '',
        address?.state ?? '',
        address?.postal_code ?? '',
        address?.country ?? '',
        address?.lat ?? '',
        address?.lng ?? '',
      ].join('/')
    })
    .sort()

  return [getRouteOptimizationOriginKey(driverLocation, floristLocation), ...stopKeys].join('|')
}

function getLastRouteUpdateTimestamp(share, stops) {
  const routeId = Number(share?.id || 0)
  const timestamps = []
  const addTimestamp = (value) => {
    const parsedValue = getSafeDayjs(value)

    if (!parsedValue) {
      return
    }

    timestamps.push({
      raw: value,
      sortValue: parsedValue.valueOf(),
    })
  }

  addTimestamp(share?.driver_location?.updated_at)
  stops.forEach((stop) => {
    addTimestamp(stop?.fulfilled_at)
    ;(stop?.delivery_attempts || []).forEach((attempt) => {
      if (Number(attempt?.delivery_share?.id || 0) !== routeId) {
        return
      }

      addTimestamp(attempt?.attempted_at)
    })
  })

  return timestamps.sort((left, right) => right.sortValue - left.sortValue)[0]?.raw
}

export function DeliveryRouteDetail({ routeApi }) {
  const navigate = useNavigate()
  const path = usePathHelper()
  const [, copyToClipboard] = useCopyToClipboard()
  const { openPrintWindow } = usePrintWindow({
    onError: (error) =>
      toast.error('Unable to print delivery list', { description: getErrorMessage(error) }),
  })
  const { isReadOnly, share } = useDeliveryRouteDetailShare({ routeApi })
  const printQrCanvasRef = useRef(null)
  const routeOptimizationInFlightRef = useRef(false)
  const updateDeliveryShare = useUpdateDeliveryShareMutation()
  const revokeDeliveryShare = useRevokeDeliveryShareMutation()
  const routeStopSensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 6 },
    }),
    useSensor(TouchSensor, {
      activationConstraint: { delay: 150, tolerance: 5 },
    })
  )
  const isCompletedRoute = isDeliveryRouteCompleted(share)
  const isStartedRoute = isDeliveryRouteStarted(share)
  const isExpiredRoute = share?.status === 'expired'
  const missedCount = getDeliveryRouteMissedCount(share)
  const stopCount = Number(share.stop_count || 0)
  const deliveredCount = Number(share.delivered_count || 0)
  const handledStopCount = Math.min(deliveredCount + missedCount, stopCount)
  const remainingStopCount = Math.max(
    Number(share.remaining_count ?? stopCount - deliveredCount - missedCount),
    0
  )
  const routeDisplayStops = buildDeliveryRouteDisplayStops(share)
  const lastRouteUpdateAt =
    getLastRouteUpdateTimestamp(share, routeDisplayStops) || share.created_at
  const hasRouteUpdateHistory = lastRouteUpdateAt !== share.created_at
  const lockedStopIds = routeDisplayStops
    .filter((stop) => stop.is_delivered)
    .map(getDeliveryRouteSelectionId)
  const lockedStopIdSet = new Set(lockedStopIds)
  const routeStopOrderIds = routeDisplayStops.map(getDeliveryRouteSelectionId)
  const reorderableRouteStopIds = routeDisplayStops
    .filter((stop) => !lockedStopIdSet.has(getDeliveryRouteSelectionId(stop)))
    .map(getDeliveryRouteSelectionId)
  const routeStopOrderKey = getDeliveryRouteReorderSessionKey(share)
  const routeStopById = new Map(
    routeDisplayStops.map((stop) => [getDeliveryRouteSelectionId(stop), stop])
  )
  const [isReorderingStops, setIsReorderingStops] = useState(false)
  const [isOptimizingRouteOrder, setIsOptimizingRouteOrder] = useState(false)
  const [lastRouteOptimization, setLastRouteOptimization] = useState(null)
  const [manualRouteStopIds, setManualRouteStopIds] = useState(reorderableRouteStopIds)
  const [mapEnabledBuckets, setMapEnabledBuckets] = useState(() =>
    getDeliveryRouteMapBucketsForOrders(routeDisplayStops)
  )
  const manualRouteStopOrderIds = getManualRouteStopOrderIds(
    routeStopOrderIds,
    manualRouteStopIds,
    lockedStopIdSet
  )
  const displayedRouteStops = isReorderingStops
    ? manualRouteStopOrderIds.map((orderId) => routeStopById.get(orderId)).filter(Boolean)
    : routeDisplayStops
  const hasManualRouteOrderChanges =
    manualRouteStopIds.join(',') !== reorderableRouteStopIds.join(',')
  const routeOptimizationProblemKey = getRouteOptimizationProblemKey(
    manualRouteStopIds,
    routeStopById,
    share.driver_location,
    share.location
  )
  const cachedOptimizedRouteStopIds =
    lastRouteOptimization?.problemKey === routeOptimizationProblemKey
      ? lastRouteOptimization.routeStopIds
      : null
  const isCurrentDraftOptimized =
    cachedOptimizedRouteStopIds?.join(',') === manualRouteStopIds.join(',')
  const isRefreshingCanonicalRoute =
    share.live_route_version &&
    share.route_version &&
    share.live_route_version !== share.route_version
  const canReorderRouteStops =
    !isReadOnly &&
    !isRefreshingCanonicalRoute &&
    !isExpiredRoute &&
    !isCompletedRoute &&
    reorderableRouteStopIds.length > 1

  useEffect(() => {
    routeOptimizationInFlightRef.current = false
    setIsReorderingStops(false)
    setIsOptimizingRouteOrder(false)
    setManualRouteStopIds(reorderableRouteStopIds)
    // The server stop order/locked ids are the reset trigger; the derived id array is rebuilt each render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [routeStopOrderKey])

  const handleCopy = async () => {
    if (!share.share_url) {
      return
    }

    await copyToClipboard(share.share_url)
    toast.success('Route link copied to clipboard')
  }

  const handleDelete = () => {
    revokeDeliveryShare.mutate(share.id, {
      onSuccess: (response) => {
        toast.success(response?.message || 'Route deleted successfully')
        void navigate({
          ...path.deliveryRoutes.index().link,
        })
      },
      onError: (error) => {
        toast.error('Unable to delete route', {
          description: getDeliveryShareErrorMessage(error),
        })
      },
    })
  }

  const handleRouteStopDragEnd = ({ active, over }) => {
    if (
      routeOptimizationInFlightRef.current ||
      updateDeliveryShare.isPending ||
      isRefreshingCanonicalRoute ||
      !over ||
      active.id === over.id
    ) {
      return
    }

    setManualRouteStopIds((currentOrderIds) => {
      const oldIndex = currentOrderIds.findIndex((orderId) => String(orderId) === String(active.id))
      const newIndex = currentOrderIds.findIndex((orderId) => String(orderId) === String(over.id))

      if (oldIndex < 0 || newIndex < 0) {
        return currentOrderIds
      }

      return arrayMove(currentOrderIds, oldIndex, newIndex)
    })
  }

  const moveManualRouteStop = (orderId, direction) => {
    if (
      routeOptimizationInFlightRef.current ||
      updateDeliveryShare.isPending ||
      isRefreshingCanonicalRoute
    ) {
      return
    }

    setManualRouteStopIds((currentOrderIds) => {
      const currentIndex = currentOrderIds.indexOf(orderId)
      const nextIndex = currentIndex + direction

      if (currentIndex < 0 || nextIndex < 0 || nextIndex >= currentOrderIds.length) {
        return currentOrderIds
      }

      return arrayMove(currentOrderIds, currentIndex, nextIndex)
    })
  }

  const handleCancelRouteReorder = () => {
    routeOptimizationInFlightRef.current = false
    setIsOptimizingRouteOrder(false)
    setManualRouteStopIds(reorderableRouteStopIds)
    setIsReorderingStops(false)
  }

  const handleOptimizedRouteOrderChange = (optimizedOrderIds) => {
    if (!routeOptimizationInFlightRef.current) {
      return
    }

    routeOptimizationInFlightRef.current = false
    setIsOptimizingRouteOrder(false)

    if (!Array.isArray(optimizedOrderIds) || optimizedOrderIds.length === 0) {
      toast.error('Unable to optimize route', {
        description:
          'Google could not calculate an optimized stop order. You can keep arranging the stops manually.',
      })
      return
    }

    const nextOrderIds = applyOptimizedRouteStopOrder(manualRouteStopIds, optimizedOrderIds)
    const didOrderChange = nextOrderIds.join(',') !== manualRouteStopIds.join(',')

    setManualRouteStopIds(nextOrderIds)
    setLastRouteOptimization({
      problemKey: routeOptimizationProblemKey,
      routeStopIds: nextOrderIds,
    })
    toast.success(
      didOrderChange
        ? 'Optimized order applied. Save to keep it.'
        : 'This route is already optimized.'
    )
  }

  const handleOptimizeRouteOrder = () => {
    if (
      routeOptimizationInFlightRef.current ||
      isCurrentDraftOptimized ||
      isRefreshingCanonicalRoute
    ) {
      return
    }

    if (cachedOptimizedRouteStopIds) {
      setManualRouteStopIds(cachedOptimizedRouteStopIds)
      toast.success('Optimized order applied. Save to keep it.')
      return
    }

    routeOptimizationInFlightRef.current = true
    setIsOptimizingRouteOrder(true)
  }

  const handleSaveManualRouteOrder = async () => {
    if (isRefreshingCanonicalRoute) {
      return
    }

    if (!hasManualRouteOrderChanges) {
      setIsReorderingStops(false)
      return
    }

    try {
      await updateDeliveryShare.mutateAsync({
        deliveryShareId: share.id,
        ...(share.route_version ? { route_version: share.route_version } : {}),
        location_id: share.location?.id,
        preserve_order: true,
        stops: manualRouteStopOrderIds
          .map((stopId) => routeStopById.get(stopId))
          .filter(Boolean)
          .map(buildDeliveryRouteStopPayload),
      })
      toast.success('Stop order saved')
      routeOptimizationInFlightRef.current = false
      setIsOptimizingRouteOrder(false)
      setIsReorderingStops(false)
    } catch (error) {
      toast.error('Unable to save stop order', {
        description: getDeliveryShareErrorMessage(error),
      })
    }
  }

  const routeName = String(share.name ?? '').trim()
  const canPrintShareQrCode = Boolean(share.share_url && !isExpiredRoute)

  const handlePrint = () => {
    let shareQrCodeDataUrl = null

    if (canPrintShareQrCode) {
      const qrCanvas = printQrCanvasRef.current

      if (!(qrCanvas instanceof HTMLCanvasElement)) {
        toast.error('The route QR code is still loading', {
          description: 'Try printing the delivery list again.',
        })
        return
      }

      try {
        shareQrCodeDataUrl = qrCanvas.toDataURL('image/png')
      } catch (error) {
        toast.error('Unable to add the route QR code', {
          description: getErrorMessage(error),
        })
        return
      }
    }

    void openPrintWindow({
      html: buildDeliveryRoutePrintHtml({
        share,
        shareQrCodeDataUrl,
        stops: routeDisplayStops,
      }),
    })
  }

  return (
    <div className="w-full max-w-none space-y-4 pb-10 xl:grid xl:h-[calc(100dvh-5.5rem)] xl:min-h-0 xl:grid-rows-[auto_auto_minmax(0,1fr)] xl:gap-4 xl:space-y-0 xl:overflow-hidden xl:pb-0">
      {canPrintShareQrCode ? (
        <div aria-hidden="true" className="pointer-events-none fixed -left-[10000px] top-0">
          <QRCodeCanvas
            ref={printQrCanvasRef}
            value={share.share_url}
            size={256}
            marginSize={4}
            level="M"
          />
        </div>
      ) : null}

      <div className="space-y-3 xl:shrink-0">
        <AdminPageHeader
          icon={RouteIcon}
          title={routeName || 'Delivery Route'}
          badges={
            <>
              {isExpiredRoute ? <Badge variant="outline">Past Route</Badge> : null}
              {isStartedRoute ? <Badge variant="info">Started</Badge> : null}
            </>
          }
          actions={
            <div className="no-scrollbar flex max-w-full min-w-0 items-center gap-2 overflow-x-auto whitespace-nowrap">
              <AdminHeaderActionButton variant="outline" onClick={handlePrint}>
                <Printer className="size-4" />
                Print List
              </AdminHeaderActionButton>
              {!isExpiredRoute ? (
                <>
                  <AdminHeaderActionButton
                    variant="outline"
                    disabled={isReadOnly || isCompletedRoute}
                    onClick={() => {
                      void navigate({
                        ...path.deliveryRoutes.edit(share.id).link,
                      })
                    }}
                  >
                    <PencilLine className="size-4" />
                    Edit Route
                  </AdminHeaderActionButton>
                  <DeliveryRouteShareDialog
                    share={share}
                    disabled={isReadOnly || updateDeliveryShare.isPending}
                    className="h-10 rounded-full px-4"
                  />
                  <AdminHeaderActionButton
                    aria-label="Copy Link"
                    variant="outline"
                    onClick={handleCopy}
                  >
                    <Copy className="size-4" />
                    Copy
                  </AdminHeaderActionButton>
                  <ConfirmationDialog
                    title="Delete delivery route?"
                    description="Anyone using this route link will lose access immediately."
                    confirmText="Delete route"
                    pendingText="Deleting..."
                    isPending={revokeDeliveryShare.isPending}
                    onConfirm={handleDelete}
                    trigger={
                      <AdminHeaderActionButton
                        variant="outline"
                        disabled={
                          isReadOnly ||
                          revokeDeliveryShare.isPending ||
                          updateDeliveryShare.isPending ||
                          isCompletedRoute
                        }
                      >
                        <Trash2 className="size-4" />
                        Delete
                      </AdminHeaderActionButton>
                    }
                  />
                </>
              ) : null}
            </div>
          }
        />
      </div>

      <div className="space-y-3">
        <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
          <RouteMetricCard
            label="Stops handled"
            value={`${handledStopCount}/${share.stop_count}`}
            description="Delivered or marked missed on this route"
            tone={
              handledStopCount > 0
                ? remainingStopCount === 0 && missedCount === 0
                  ? 'success'
                  : 'brand'
                : 'neutral'
            }
          />
          <RouteMetricCard
            label="Open stops"
            value={`${remainingStopCount}`}
            description={
              remainingStopCount === 0 ? 'No stops still waiting' : 'Still need driver action'
            }
            tone={remainingStopCount > 0 ? 'brand' : 'neutral'}
          />
          <RouteMetricCard
            label="Missed stops"
            value={`${missedCount}`}
            description={missedCount > 0 ? 'Need florist follow-up' : 'No missed stops logged'}
            tone={missedCount > 0 ? 'destructive' : 'neutral'}
          />
          <RouteMetricCard
            label="Last update"
            value={formatDeliveryRouteDateTime(lastRouteUpdateAt) || 'No updates yet'}
            description={
              hasRouteUpdateHistory ? 'Most recent driver or stop update' : 'Route created'
            }
          />
        </div>
      </div>

      <div className="grid gap-4 xl:h-full xl:min-h-0 xl:grid-cols-[minmax(28rem,1.25fr)_minmax(22rem,0.75fr)] xl:items-stretch xl:overflow-hidden">
        <Card size="sm" className="flex min-h-0 min-w-0 flex-col overflow-hidden">
          <CardHeader size="sm">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
              <div className="min-w-0 space-y-1">
                <CardTitle size="sm">Stops</CardTitle>
                <CardDescription size="sm">
                  Delivered and missed stops are clearly marked so you can see what happened on this
                  route.
                </CardDescription>
              </div>
              {isReorderingStops || canReorderRouteStops ? (
                isReorderingStops ? (
                  <div className="flex shrink-0 flex-wrap items-center gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={handleCancelRouteReorder}
                      disabled={updateDeliveryShare.isPending || isOptimizingRouteOrder}
                    >
                      <X className="size-4" />
                      Cancel
                    </Button>
                    <LoadingButton
                      type="button"
                      variant="outline"
                      size="sm"
                      isPending={isOptimizingRouteOrder}
                      disabled={
                        updateDeliveryShare.isPending ||
                        isCurrentDraftOptimized ||
                        isRefreshingCanonicalRoute
                      }
                      onClick={handleOptimizeRouteOrder}
                    >
                      <Sparkles className="size-4" />
                      {isCurrentDraftOptimized ? 'Route optimized' : 'Optimize route'}
                    </LoadingButton>
                    <LoadingButton
                      type="button"
                      variant="default"
                      size="sm"
                      isPending={updateDeliveryShare.isPending}
                      disabled={isOptimizingRouteOrder || isRefreshingCanonicalRoute}
                      onClick={handleSaveManualRouteOrder}
                    >
                      Save
                    </LoadingButton>
                  </div>
                ) : (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="shrink-0"
                    onClick={() => {
                      routeOptimizationInFlightRef.current = false
                      setIsOptimizingRouteOrder(false)
                      setIsReorderingStops(true)
                    }}
                  >
                    <GripVertical className="size-4" />
                    Reorder stops
                  </Button>
                )
              ) : null}
            </div>
          </CardHeader>
          <CardContent size="sm" className="min-h-0 flex-1 overflow-hidden">
            <ScrollArea
              className="h-[32rem] min-h-0 overflow-hidden xl:h-full"
              viewportClassName="rounded-none"
            >
              <div className="pr-3">
                {isReorderingStops ? (
                  <DndContext
                    sensors={routeStopSensors}
                    collisionDetection={closestCenter}
                    onDragEnd={handleRouteStopDragEnd}
                  >
                    <SortableContext
                      items={displayedRouteStops.map((stop) =>
                        String(getDeliveryRouteSelectionId(stop))
                      )}
                      strategy={verticalListSortingStrategy}
                    >
                      <div className="space-y-3">
                        {displayedRouteStops.map((stop, index) => {
                          const selectionId = getDeliveryRouteSelectionId(stop)
                          const reorderableIndex = manualRouteStopIds.indexOf(selectionId)

                          return (
                            <SortableRouteStopCard
                              key={selectionId}
                              order={stop}
                              stopNumber={index + 1}
                              isReordering
                              isLocked={lockedStopIdSet.has(selectionId)}
                              isReorderPending={
                                isOptimizingRouteOrder || updateDeliveryShare.isPending
                              }
                              reorderableIndex={reorderableIndex}
                              reorderableCount={manualRouteStopIds.length}
                              onMoveUp={() => moveManualRouteStop(selectionId, -1)}
                              onMoveDown={() => moveManualRouteStop(selectionId, 1)}
                            />
                          )
                        })}
                      </div>
                    </SortableContext>
                  </DndContext>
                ) : (
                  <div className="space-y-3">
                    {routeDisplayStops.map((stop, index) => (
                      <DeliveryRouteStopCard
                        key={getDeliveryRouteSelectionId(stop)}
                        order={stop}
                        stopNumber={index + 1}
                      />
                    ))}
                  </div>
                )}
              </div>
            </ScrollArea>
          </CardContent>
        </Card>

        <div className="min-w-0 xl:min-h-0">
          <DeliveryRouteBuilderMap
            orders={routeDisplayStops}
            anchorOrder={routeDisplayStops[0]}
            driverLocation={share.driver_location}
            enabledBuckets={mapEnabledBuckets}
            highlightedOrderIds={[]}
            isReadOnly
            pinnedOrderIds={routeStopOrderIds}
            routeOrderMode={isOptimizingRouteOrder ? 'optimized' : 'ordered'}
            selectedOrderIds={isReorderingStops ? manualRouteStopOrderIds : routeStopOrderIds}
            onEnabledBucketsChange={setMapEnabledBuckets}
            onOptimizedRouteOrderChange={
              isOptimizingRouteOrder ? handleOptimizedRouteOrderChange : undefined
            }
          />
        </div>
      </div>
    </div>
  )
}
